import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useConcierge } from "@/context/ConciergeContext";
import { ArrowRight, Minus, Plus } from "lucide-react";

const CATEGORIES = [
  { slug: "footwear", name: "Footwear", note: "Shoes, sneakers, sandals & more", img: "https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=800&q=70" },
  { slug: "apparel", name: "Apparel", note: "Everyday, active & occasion", img: "https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=800&q=70" },
  { slug: "sports-equipment", name: "Sports & Outdoors", note: "Train, play, explore", img: "https://images.unsplash.com/photo-1501554728187-ce583db33af7?w=800&q=70" },
];

const STEPS = [
  { n: "01", h: "Find", c: "Discover products from independent, vetted vendors — not an endless feed." },
  { n: "02", h: "Verify", c: "Listings are reviewed and products undergo the applicable Spotter inspection." },
  { n: "03", h: "Match", c: "Use your size, needs and budget to narrow the marketplace to what fits you." },
  { n: "04", h: "Buy", c: "Order directly from the vendor and track everything through The Spotters." },
];

const CRITERIA = [
  { t: "Authenticity", d: "We check whether the product and its representation align with the applicable authenticity criteria." },
  { t: "Condition", d: "We inspect the physical condition against the product information the vendor submitted." },
  { t: "Construction", d: "We assess build quality — stitching, materials and finish — for the product category." },
  { t: "Listing accuracy", d: "We verify the listing matches the physical product: brand, model, colour and specifications." },
  { t: "Size & spec compliance", d: "Where applicable, we check sizing and specifications against BIS and declared information." },
];

const JOURNAL = [
  { tag: "The Know", title: "How to read a shoe size chart before you buy online", img: "https://images.unsplash.com/photo-1460353581641-37baddab0fa2?w=600&q=70" },
  { tag: "The Field", title: "Inside a Spotter inspection: what actually gets checked", img: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=600&q=70" },
  { tag: "The Find", title: "Five everyday sneakers worth a second look", img: "https://images.unsplash.com/photo-1549298916-b41d501d3772?w=600&q=70" },
];

// Swap this for your own studio product-composition photo when ready.
const HERO_IMAGE = "https://images.unsplash.com/photo-1600185365483-26d7a4cc7519?w=1000&q=75";

function Eyebrow({ children, light, className = "" }) {
  return <p className={`font-mono text-[10px] uppercase tracking-[0.22em] ${light ? "text-background/55" : "text-muted-foreground"} ${className}`}>{children}</p>;
}

function SpotterSeal({ className = "" }) {
  return (
    <svg viewBox="0 0 120 120" className={className} xmlns="http://www.w3.org/2000/svg" aria-label="Spotter Certified">
      <defs><path id="sealArc" d="M60 60 m-45 0 a45 45 0 1 1 90 0 a45 45 0 1 1 -90 0" /></defs>
      <circle cx="60" cy="60" r="58" fill="hsl(var(--background))" stroke="hsl(var(--accent))" strokeWidth="1.5" />
      <circle cx="60" cy="60" r="31" fill="none" stroke="hsl(var(--accent))" strokeWidth="0.75" opacity="0.6" />
      <text fill="hsl(var(--accent))" fontFamily="'JetBrains Mono', monospace" fontSize="9.5" letterSpacing="2.6">
        <textPath href="#sealArc" startOffset="1%">SPOTTER CERTIFIED · № TS-2026 · INSPECTED ·</textPath>
      </text>
      <text x="60" y="70" textAnchor="middle" fill="hsl(var(--accent))" fontFamily="Georgia, serif" fontSize="30">✦</text>
    </svg>
  );
}

// Interactive BIS demo — enter your size, see the match verdict.
function SizeMatch() {
  const PRODUCT = 9;
  const [size, setSize] = useState(9);
  const diff = Math.abs(size - PRODUCT);
  const verdict = diff === 0 ? { t: "Match", ok: 1 } : diff <= 0.5 ? { t: "Close match", ok: 0.5 } : { t: "Off by a size", ok: 0 };
  return (
    <div className="border border-background/30 p-8">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs uppercase tracking-widest text-background/60">Your size</span>
        <div className="inline-flex items-center border border-background/30">
          <button onClick={() => setSize((s) => Math.max(3, Math.round((s - 0.5) * 2) / 2))} aria-label="decrease" className="w-8 h-8 flex items-center justify-center hover:bg-background/10"><Minus size={13} /></button>
          <span className="w-12 text-center font-serif text-2xl">{size}</span>
          <button onClick={() => setSize((s) => Math.min(14, Math.round((s + 0.5) * 2) / 2))} aria-label="increase" className="w-8 h-8 flex items-center justify-center hover:bg-background/10"><Plus size={13} /></button>
        </div>
      </div>
      <div className="h-px bg-background/20 my-5" />
      <div className="flex items-center justify-between font-mono text-xs uppercase tracking-widest text-background/60">
        <span>Product size</span><span className="text-background text-2xl font-serif">{PRODUCT}</span>
      </div>
      <div className={`mt-6 text-center py-3 font-mono text-xs uppercase tracking-[0.2em] ${verdict.ok === 1 ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : verdict.ok === 0.5 ? "border border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border border-background/30 text-background/60"}`}>
        {verdict.ok ? "✓ " : ""}{verdict.t}
      </div>
      <p className="mt-3 font-mono text-[10px] text-background/40 text-center">Demo · see live matching on any product page</p>
    </div>
  );
}

export default function Landing() {
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [openCrit, setOpenCrit] = useState(0);
  const { openConcierge } = useConcierge();

  useEffect(() => {
    api.get("/products").then((r) => setProducts((r.data || []).slice(0, 8))).catch(() => {});
    api.get("/vendors?status=approved").then((r) => setVendors((r.data || []).slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div>
      {/* 01 · HERO */}
      <section className="border-b border-foreground overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-6 md:pt-8 pb-10 md:pb-14 grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-7">
            <Eyebrow className="whitespace-nowrap">Multi-Vendor Marketplace · India</Eyebrow>
            <h1 data-testid="hero-headline" className="font-serif font-black text-[3.4rem] leading-[0.92] md:text-8xl md:leading-[0.88] tracking-tighter mt-6">
              Buy with a little<br />more <em className="text-[hsl(var(--accent))] not-italic">certainty.</em>
            </h1>
            <p className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-muted-foreground">
              Every product on The Spotters earns its place. We inspect, verify and curate products from
              independent vendors — so you can shop with more confidence.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-btn"
                className="inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 font-mono text-[11px] uppercase tracking-widest hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))] transition-colors">
                Shop The Spotters <ArrowRight size={16} />
              </Link>
              <button onClick={() => openConcierge()} data-testid="hero-concierge-btn"
                className="inline-flex items-center gap-3 border border-foreground px-8 py-4 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors">
                Let a Spotter help
              </button>
            </div>
            <p className="mt-7 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Spotter Certified · Verified Vendors · BIS Size Match · GST Included
            </p>
          </div>
          <div className="md:col-span-5 relative">
            <div className="aspect-[4/5] bg-secondary relative overflow-hidden">
              <img src={HERO_IMAGE} alt="Spotter Certified products" className="absolute inset-0 w-full h-full object-cover" />
            </div>
            <SpotterSeal className="absolute -top-6 -right-4 md:-right-8 w-24 h-24 md:w-28 md:h-28 drop-shadow-sm" />
            <div className="absolute -bottom-6 -left-6 md:-left-12 bg-background border-2 border-foreground p-6 max-w-[280px] tag-punch pl-10">
              <Eyebrow>Batch Inspection · Delhi Hub</Eyebrow>
              <p className="font-serif text-2xl mt-1 leading-tight">"Stitching count: 8/in. Approved."</p>
              <p className="font-mono text-[10px] mt-2">— The Perfectionist</p>
            </div>
          </div>
        </div>
      </section>

      {/* 02 · PERSONALIZED DISCOVERY */}
      <section className="border-b border-foreground bg-secondary/40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-14 md:py-16 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <Eyebrow>Personalized discovery</Eyebrow>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tighter mt-2">Don't know where to start? Let a Spotter help.</h2>
            <p className="mt-4 text-muted-foreground max-w-xl">
              Tell us what you're looking for and we'll narrow the marketplace to products that fit you.
            </p>
            <button onClick={() => openConcierge()} data-testid="find-picks-btn"
              className="mt-7 inline-flex items-center gap-3 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-8 py-4 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors">
              Find my picks <ArrowRight size={16} />
            </button>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">60 seconds · No sign-up required</p>
          </div>
          <div className="md:col-span-5">
            <div className="grid grid-cols-2 border-t border-l border-foreground/20">
              {["Needs", "Style", "Budget", "Size"].map((x, i) => (
                <div key={x} className="border-b border-r border-foreground/20 p-6">
                  <span className="font-mono text-[10px] text-muted-foreground">0{i + 1}</span>
                  <p className="font-serif text-2xl tracking-tight mt-1">{x}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 03 · WHERE TO LOOK */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-16 md:py-20">
        <Eyebrow>Shop the Spotters</Eyebrow>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-2 mb-10">Where to look.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {CATEGORIES.map((c) => (
            <Link key={c.slug} to={`/shop?category=${c.slug}`} className="group relative aspect-[4/3] overflow-hidden bg-secondary">
              <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />
              <div className="absolute inset-0 p-7 flex flex-col justify-end text-background">
                <span className="font-mono text-[10px] uppercase tracking-widest text-background/70">{c.note}</span>
                <h3 className="font-serif text-3xl md:text-4xl tracking-tighter mt-1">{c.name}</h3>
                <span className="mt-2 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest">Explore <ArrowRight size={14} /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 04 · CURRENTLY SPOTTED — products up front */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <Eyebrow>Currently spotted</Eyebrow>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-2">Worth a look.</h2>
            <p className="mt-3 text-muted-foreground max-w-md">Products we've been paying attention to.</p>
          </div>
          <Link to="/shop" data-testid="see-all-btn" className="font-mono text-[11px] uppercase tracking-widest border-b border-foreground pb-1 hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))] whitespace-nowrap">See all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
          {products.map((p) => {
            const sizes = p.sizes || [];
            const sizeRange = sizes.length > 1 ? `${sizes[0]}–${sizes[sizes.length - 1]}` : sizes[0];
            return (
              <Link key={p.id} to={`/product/${p.id}`} data-testid={`product-card-${p.id}`} className="group border border-foreground/15 hover:border-foreground transition-colors flex flex-col">
                <div className="relative aspect-square overflow-hidden bg-secondary">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                  {p.spotted_certificate?.passed && (
                    <span className="absolute top-3 left-3 bg-background/90 border border-foreground/20 px-2 py-1 font-mono text-[8px] uppercase tracking-widest">✦ Spotter Certified</span>
                  )}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{p.vendor_name}</p>
                  <h3 className="font-serif text-lg mt-1 leading-tight line-clamp-1">{p.title}</h3>
                  <div className="flex items-center justify-between mt-2">
                    <p className="font-mono text-sm">₹ {Number(p.price || 0).toLocaleString("en-IN")}</p>
                    {p.rating > 0 && <span className="font-mono text-[10px] text-muted-foreground">★ {Number(p.rating).toFixed(1)}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 font-mono text-[10px] text-muted-foreground">
                    {sizeRange && <span>Sizes {sizeRange}</span>}
                    {p.category === "footwear" && <span className="text-[hsl(var(--accent))]">◆ BIS Match</span>}
                  </div>
                  <span className="mt-auto pt-3 border-t border-foreground/10 font-mono text-[9px] uppercase tracking-widest text-[hsl(var(--accent))] inline-flex items-center gap-1">Why we spotted it <ArrowRight size={11} /></span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 05 · THE DIFFERENCE — the proof */}
      <section className="bg-foreground text-background">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-7">
            <Eyebrow light>The difference</Eyebrow>
            <h2 className="font-serif text-5xl md:text-8xl tracking-tighter mt-3 leading-[0.92]">Every product.<br />Physically inspected.</h2>
          </div>
          <div className="md:col-span-5 flex flex-col justify-end">
            <p className="text-background/75 leading-relaxed max-w-md text-lg">
              Products don't simply appear on The Spotters. Vendors submit them. Our Spotters inspect them.
              We verify the listing. Then you decide.
            </p>
            <Link to="/process" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest border-b border-background/60 pb-1 w-fit hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))]">
              See how it works <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* 06 · HOW IT WORKS — connected timeline */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-24">
        <Eyebrow>How the Spotters work</Eyebrow>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-2 mb-14">Four steps. No guesswork.</h2>
        <div className="hidden md:flex items-center mb-8">
          {STEPS.map((s, i) => (
            <div key={s.n} className="flex items-center" style={{ flex: i < STEPS.length - 1 ? 1 : "0 0 auto" }}>
              <span className="font-serif text-4xl text-[hsl(var(--accent))] w-14">{s.n}</span>
              {i < STEPS.length - 1 && (
                <span className="flex-1 flex items-center"><span className="flex-1 h-px bg-foreground/25" /><ArrowRight size={14} className="text-foreground/40 -ml-1" /></span>
              )}
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-4 gap-10 md:gap-6">
          {STEPS.map((s) => (
            <div key={s.n}>
              <span className="font-serif text-4xl text-[hsl(var(--accent))] md:hidden">{s.n}</span>
              <h3 className="font-serif text-2xl tracking-tight md:mt-0 mt-2">{s.h}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground max-w-[260px]">{s.c}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 07 · SPOTTER CERTIFIED — interactive criteria */}
      <section className="border-y border-foreground bg-secondary/40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-24 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Eyebrow>The mark</Eyebrow>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-2">What "Spotter Certified" means.</h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
              Not everything submitted makes it through. Products are assessed against the applicable Spotter
              inspection criteria before they earn the mark.
            </p>
            <Link to="/process" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest border-b border-foreground pb-1 w-fit hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))]">
              See our certification process <ArrowRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-7">
            <ul className="border-t border-foreground/15">
              {CRITERIA.map((c, i) => (
                <li key={c.t} className="border-b border-foreground/15">
                  <button onClick={() => setOpenCrit(openCrit === i ? -1 : i)} className="w-full flex items-center gap-5 py-4 text-left">
                    <span className="font-mono text-[10px] text-muted-foreground w-6">0{i + 1}</span>
                    <span className="font-serif text-2xl md:text-3xl tracking-tight flex-1">{c.t}</span>
                    <span className="font-mono text-lg text-[hsl(var(--accent))]">{openCrit === i ? "–" : "+"}</span>
                  </button>
                  {openCrit === i && <p className="pb-5 pl-11 pr-4 text-sm text-muted-foreground max-w-xl leading-relaxed">{c.d}</p>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 08 · BIS SIZE MATCH — interactive */}
      <section className="bg-foreground text-background">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <Eyebrow light>BIS Size Match · Live</Eyebrow>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-3 leading-[0.95]">Size should be a confidence, not a guess.</h2>
            <p className="mt-6 text-background/75 leading-relaxed max-w-lg">
              Where supported, The Spotters helps you compare a product's sizing against applicable BIS sizing
              information — so you see size guidance before you buy, not after it arrives.
            </p>
            <Link to="/size-match" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest border-b border-background/60 pb-1 w-fit hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))]">
              See how Size Match works <ArrowRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-5"><SizeMatch /></div>
        </div>
      </section>

      {/* 09 · MEET THE MAKERS */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <Eyebrow>The makers</Eyebrow>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-2 leading-none">Good products come from good makers.</h2>
            <p className="mt-6 text-muted-foreground text-sm leading-relaxed max-w-sm">
              Independent vendors get a place to be discovered — without asking you to take their word for
              everything. We keep watching them after they go live.
            </p>
            <Link to="/vendors" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest border-b border-foreground pb-1 w-fit hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))]">
              Meet our vendors <ArrowRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {vendors.map((v) => {
              const img = v.banner_url || v.logo_url;
              const score = Math.max(0, Math.min(100, Math.round(100 - (Number(v.failure_rate) || 0))));
              return (
                <Link key={v.id} to={`/vendor/${v.slug}`} data-testid={`vendor-card-${v.slug}`}
                  className="group border border-foreground hover:bg-foreground hover:text-background transition-colors overflow-hidden">
                  <div className="w-full aspect-[4/3] bg-secondary overflow-hidden">
                    {img && <img src={img} alt={v.store_name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif text-lg leading-tight">{v.store_name}</h3>
                    <p className="text-[11px] mt-1 text-muted-foreground group-hover:text-background/60 line-clamp-1">{v.tagline}</p>
                    <div className="mt-3 pt-2 border-t border-foreground/10 group-hover:border-background/20 flex items-center justify-between">
                      <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground group-hover:text-background/60">{v.product_count || 0} products</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground group-hover:text-background/60">Score <span className="font-serif text-sm text-[hsl(var(--accent))]">{score}</span></span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* 10 · JOURNAL */}
      <section className="border-t border-foreground">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <Eyebrow>The Spotters Journal</Eyebrow>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mt-2">Read the room.</h2>
            </div>
            <Link to="/journal" className="font-mono text-[11px] uppercase tracking-widest border-b border-foreground pb-1 hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))] whitespace-nowrap">Read the Journal →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {JOURNAL.map((j) => (
              <Link key={j.title} to="/journal" className="group">
                <div className="aspect-[16/10] overflow-hidden bg-secondary">
                  <img src={j.img} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <span className="mt-4 inline-block font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">{j.tag}</span>
                <h3 className="font-serif text-2xl tracking-tight leading-snug mt-1">{j.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 11 · FINAL CTA */}
      <section className="bg-foreground text-background">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-28 text-center">
          <h2 className="font-serif text-5xl md:text-8xl tracking-tighter leading-[0.95]">Shop less randomly.<br />Shop The Spotters.</h2>
          <p className="mt-8 text-background/70 max-w-xl mx-auto leading-relaxed">
            Products are submitted by vendors. Products are inspected. Listings are verified. You choose what fits.
          </p>
          <Link to="/shop" className="mt-10 inline-flex items-center gap-3 bg-background text-foreground px-10 py-4 font-mono text-[11px] uppercase tracking-widest hover:bg-[hsl(var(--accent))] hover:text-background transition-colors">
            Explore the marketplace <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
